
import { useState, Fragment } from 'react';
import { ExpenseData } from '../types';
import { DAILY_INVOICE_TYPES, BUSINESS_INVOICE_TYPES } from '../types';
import { saveExpenseReport } from '../../../services/expenses';
import { exportExpenseToExcel } from '../../../utils/excel-export';

interface ReportGenerationStepProps {
  data: ExpenseData;
  onPrev: () => void;
}

export default function ReportGenerationStep({ data, onPrev }: ReportGenerationStepProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [collapsedTypes, setCollapsedTypes] = useState<Set<string>>(new Set());
  const [showEmptyTypes, setShowEmptyTypes] = useState(false);
  const currencySymbol = data.currencySymbol || '¥';

  const invoiceTypes = data.expenseType === 'daily' ? DAILY_INVOICE_TYPES : BUSINESS_INVOICE_TYPES;

  // 按类型分组发票（自定义类型使用 customType 作为显示名称）
  const groupedInvoices = data.invoices.reduce((acc, invoice) => {
    // 对于自定义类型，使用 customType 作为分组键；否则使用 type
    const groupKey = invoice.type === '自定义' && invoice.customType
      ? invoice.customType
      : invoice.type;

    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(invoice);
    return acc;
  }, {} as Record<string, typeof data.invoices>);

  // 生成完整的分项金额表（包含0金额的项目）
  // 先处理预定义类型（排除"自定义"）
  const predefinedTypes = invoiceTypes.filter(type => type !== '自定义');
  const fullTypeSummary = predefinedTypes.map(type => {
    const invoices = groupedInvoices[type] || [];
    return {
      type,
      count: invoices.length,
      amount: invoices.reduce((sum, inv) => sum + inv.amount, 0),
      invoices: invoices
    };
  });

  // 添加所有自定义类型
  Object.keys(groupedInvoices).forEach(key => {
    // 如果这个 key 不在预定义类型中，说明是自定义类型
    if (!predefinedTypes.includes(key)) {
      const invoices = groupedInvoices[key];
      fullTypeSummary.push({
        type: key, // 使用 customType 的值
        count: invoices.length,
        amount: invoices.reduce((sum, inv) => sum + inv.amount, 0),
        invoices: invoices
      });
    }
  });

  // 分离有内容和无内容的类型
  const typesWithContent = fullTypeSummary.filter(item => item.amount > 0);
  const typesWithoutContent = fullTypeSummary.filter(item => item.amount === 0);

  // 生成汇总表（只显示有金额的项目）
  const summaryWithAmount = fullTypeSummary.filter(item => item.amount > 0);

  // 初始化折叠状态 - 金额为0的类型默认折叠
  useState(() => {
    const zeroAmountTypes = new Set(
      fullTypeSummary.filter(item => item.amount === 0).map(item => item.type)
    );
    setCollapsedTypes(zeroAmountTypes);
  });

  const toggleTypeCollapse = (type: string) => {
    setCollapsedTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  const generateReport = async () => {
    setIsGenerating(true);

    try {
      // Save expense report to LeanCloud
      const reportId = await saveExpenseReport(data, 'user-' + Date.now());
      console.log('Report saved successfully with ID:', reportId);

      setIsGenerating(false);
      setShowSuccess(true);
    } catch (error: any) {
      console.error('Failed to save report:', error);
      alert('保存报销单失败: ' + error.message);
      setIsGenerating(false);
    }
  };

  const downloadExcel = () => {
    try {
      exportExpenseToExcel(data);
    } catch (error: any) {
      console.error('Failed to export Excel:', error);
      alert('导出Excel失败: ' + error.message);
    }
  };

  const numberToChinese = (num: number): string => {
    const digits = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
    const units = ['', '拾', '佰', '仟', '万', '拾', '佰', '仟', '亿'];

    if (num === 0) return '零元整';

    const numStr = Math.floor(num).toString();
    let result = '';

    for (let i = 0; i < numStr.length; i++) {
      const digit = parseInt(numStr[i]);
      const unit = units[numStr.length - 1 - i];
      result += digits[digit] + unit;
    }

    return result + '元整';
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">生成报销单</h2>
        <p className="text-gray-600">确认信息无误后，生成并导出报销单据</p>
      </div>

      {/* 报销信息汇总 */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">报销信息汇总</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-sm text-gray-500">报销人</div>
            <div className="font-medium">{data.userName}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">报销类型</div>
            <div className="font-medium">{data.expenseType === 'daily' ? '日常报销' : '出差报销'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">发票数量</div>
            <div className="font-medium">{data.invoices.length} 张</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">总金额</div>
            <div className="font-medium text-blue-600">{currencySymbol}{data.totalAmount.toFixed(2)}</div>
          </div>
        </div>
        {data.projectName && (
          <div className="mt-4 pt-4 border-t border-blue-200">
            <div className="text-sm text-gray-500">项目信息</div>
            <div className="font-medium">{data.projectName} {data.projectCode && `(${data.projectCode})`}</div>
          </div>
        )}
      </div>

      {/* 报销单（汇总表，只显示有金额的项目） */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-6 py-3 border-b">
          <h3 className="font-semibold text-gray-800">报销单</h3>
        </div>
        <div className="p-6 space-y-6">
          {/* 基本信息 */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500">填报时间：</span>
              <span>{new Date().toLocaleDateString()}</span>
            </div>
            <div>
              <span className="text-gray-500">附件：</span>
              <span>{data.invoices.length} 张发票</span>
            </div>
            <div>
              <span className="text-gray-500">报销人：</span>
              <span>{data.userName}</span>
            </div>
            <div>
              <span className="text-gray-500">报销金额合计：</span>
              <span className="font-medium text-blue-600">{currencySymbol}{data.totalAmount.toFixed(2)}</span>
            </div>
            <div className="md:col-span-2">
              <span className="text-gray-500">核实金额（大写）：</span>
              <span className="font-medium whitespace-nowrap">{numberToChinese(data.totalAmount)}</span>
            </div>
          </div>

          {/* 费用项目明细 */}
          <div>
            <h4 className="font-medium text-gray-800 mb-3">费用项目明细</h4>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 border-r">费用项目</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">金额</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {summaryWithAmount.map((item) => (
                    <tr key={item.type}>
                      <td className="px-4 py-2 text-sm text-gray-900 border-r">{item.type}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{currencySymbol}{item.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* 分项金额表（显示每个类型下的每张发票） */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-6 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">分项金额表</h3>
          {typesWithoutContent.length > 0 && (
            <button
              onClick={() => setShowEmptyTypes(!showEmptyTypes)}
              className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer whitespace-nowrap flex items-center"
            >
              <i className={`ri-eye${showEmptyTypes ? '-off' : ''}-line mr-1`}></i>
              {showEmptyTypes ? '隐藏' : '显示'}空费用类型 ({typesWithoutContent.length})
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">费用项目</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">发票号</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">日期</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">金额</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {/* 有内容的类型（优先显示） */}
              {typesWithContent.map((typeGroup) => (
                <Fragment key={typeGroup.type}>
                  {/* 类型标题行 */}
                  <tr className="bg-blue-50">
                    <td className="px-6 py-3">
                      <button
                        onClick={() => toggleTypeCollapse(typeGroup.type)}
                        className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer"
                      >
                        <i className={`ri-arrow-${collapsedTypes.has(typeGroup.type) ? 'right' : 'down'}-s-line text-gray-500`}></i>
                        <span>{typeGroup.type}</span>
                        <span className="text-gray-500">({typeGroup.count}张)</span>
                      </button>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500">-</td>
                    <td className="px-6 py-3 text-sm text-gray-500">-</td>
                    <td className="px-6 py-3 text-sm font-medium text-blue-600">{currencySymbol}{typeGroup.amount.toFixed(2)}</td>
                  </tr>

                  {/* 发票详情行 */}
                  {!collapsedTypes.has(typeGroup.type) && (
                    typeGroup.invoices.map((invoice) => (
                      <tr key={`${typeGroup.type}-${invoice.id}`} className="bg-white">
                        <td className="px-6 py-2 pl-12 whitespace-nowrap text-sm text-gray-600">└ 明细</td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{invoice.number}</td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{invoice.date}</td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{currencySymbol}{invoice.amount.toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </Fragment>
              ))}

              {/* 无内容的类型（可选显示） */}
              {showEmptyTypes && typesWithoutContent.map((typeGroup) => (
                <Fragment key={typeGroup.type}>
                  {/* 类型标题行 */}
                  <tr className="bg-gray-50">
                    <td className="px-6 py-3">
                      <button
                        onClick={() => toggleTypeCollapse(typeGroup.type)}
                        className="flex items-center space-x-2 text-sm font-medium text-gray-500 hover:text-gray-700 cursor-pointer"
                      >
                        <i className={`ri-arrow-${collapsedTypes.has(typeGroup.type) ? 'right' : 'down'}-s-line text-gray-400`}></i>
                        <span>{typeGroup.type}</span>
                        <span className="text-gray-400">(0张)</span>
                      </button>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-400">-</td>
                    <td className="px-6 py-3 text-sm text-gray-400">-</td>
                    <td className="px-6 py-3 text-sm font-medium text-gray-400">{currencySymbol}0.00</td>
                  </tr>

                  {/* 无发票提示行 */}
                  {!collapsedTypes.has(typeGroup.type) && (
                    <tr className="bg-white">
                      <td className="px-6 py-2 pl-12 whitespace-nowrap text-sm text-gray-400">└ 无发票</td>
                      <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-400">-</td>
                      <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-400">-</td>
                      <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-400">{currencySymbol}0.00</td>
                    </tr>
                  )}
                </Fragment>
              ))}

              <tr className="bg-blue-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">合计</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{data.invoices.length} 张</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">-</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">{currencySymbol}{data.totalAmount.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* 出差申请单预览（仅出差报销显示） */}
      {data.expenseType === 'business' && (() => {
        // 从出差补贴发票中提取出差信息
        const allowanceInvoices = data.invoices.filter(inv => inv.isAllowance);

        // 提取起始日期（最早的日期）
        const startDate = allowanceInvoices.length > 0
          ? allowanceInvoices.reduce<string>((earliest, inv) => {
              const invStartDate = inv.startDate || '';
              return !earliest || (invStartDate && invStartDate < earliest) ? invStartDate : earliest;
            }, '')
          : '';

        // 提取结束日期（最晚的日期）
        const endDate = allowanceInvoices.length > 0
          ? allowanceInvoices.reduce<string>((latest, inv) => {
              const invEndDate = inv.endDate || '';
              return !latest || (invEndDate && invEndDate > latest) ? invEndDate : latest;
            }, '')
          : '';

        // 提取所有城市（去重）
        const cities = allowanceInvoices.length > 0
          ? [...new Set(allowanceInvoices.map(inv => inv.city).filter((city): city is string => Boolean(city)))]
          : [];
        const destination = cities.length > 0 ? cities.join('、') : '';

        return (
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-6 py-3 border-b">
              <h3 className="font-semibold text-gray-800">出差申请单 &amp; 费用表</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">出差人：</span>
                  <span>{data.userName}</span>
                </div>
                <div>
                  <span className="text-gray-500">项目：</span>
                  <span>{data.projectName || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">项目管理编号：</span>
                  <span>{data.projectCode || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">起始日期：</span>
                  <span>{startDate || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">结束日期：</span>
                  <span>{endDate || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">目的地：</span>
                  <span>{destination || '-'}</span>
                </div>
              </div>

              {/* 费用项目明细 */}
              <div className="pt-4 border-t">
                <h4 className="font-medium text-gray-800 mb-3">费用项目明细</h4>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 border-r">费用项目</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">金额</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {summaryWithAmount.map((item) => (
                        <tr key={item.type}>
                          <td className="px-4 py-2 text-sm text-gray-900 border-r">{item.type}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{currencySymbol}{item.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 生成按钮 */}
      {!showSuccess && (
        <div className="text-center">
          <button
            onClick={generateReport}
            disabled={isGenerating}
            className={`px-8 py-4 rounded-lg font-medium text-lg transition-all cursor-pointer whitespace-nowrap ${
              isGenerating
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700 shadow-lg hover:shadow-xl'
            }`}
          >
            {isGenerating ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                正在生成报销单...
              </>
            ) : (
              <>
                <i className="ri-file-excel-2-line mr-2"></i>
                生成并导出Excel报销单
              </>
            )}
          </button>
        </div>
      )}

      {/* 成功提示 */}
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
            <i className="ri-check-line text-2xl text-green-600"></i>
          </div>
          <h3 className="text-lg font-semibold text-green-800 mb-2">报销单生成成功！</h3>
          <p className="text-green-600 mb-4">
            请将生成的报销单与发票保存至一个文件夹，并放入指定路径。
          </p>
          <div className="space-x-4">
            <button
              onClick={downloadExcel}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-line mr-2"></i>
              下载Excel文件
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-2"></i>
              新建报销单
            </button>
          </div>
        </div>
      )}

      {/* 导航按钮 */}
      {!showSuccess && (
        <div className="flex justify-between pt-6">
          <button
            onClick={onPrev}
            className="px-8 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            上一步
          </button>
          <div></div>
        </div>
      )}
    </div>
  );
}
